import { Link } from "react-router-dom";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { Lock, ArrowRight, BookOpen } from "lucide-react";
import { SAMPLE_SCORES, SAMPLE_REPORT } from "../data/sample_report";
import { trackAndSend } from "../lib/metaEvents";
import { useOnce } from "../lib/useOnce";
import { trackCta } from "../lib/analytics";

const READING_TONE = {
  "very weak": "text-[#B8863C]",
  "weak": "text-[#B8863C]",
  "average": "text-[#78716C]",
  "solid": "text-[#1A1915]",
  "strong": "text-[#1A1915]",
};

function reading(v) {
  if (v < 2.0) return "very weak";
  if (v < 2.5) return "weak";
  if (v < 3.5) return "average";
  if (v <= 4.0) return "solid";
  return "strong";
}

export default function Preview() {
  useOnce(() => {
    trackAndSend("ViewContent", {
      content_name: "AuthorityOS Sample Report",
      content_type: "product",
      content_ids: ["authority_os_diagnostic_97"],
      value: 97.0,
      currency: "USD",
    });
  });

  const habitRadar = Object.entries(SAMPLE_SCORES.habit_scores).map(([k, v]) => ({ dim: k, score: v }));
  const effectRadar = Object.entries(SAMPLE_SCORES.effect_scores).map(([k, v]) => ({ dim: k, score: v }));

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-16 lg:py-20 relative" data-testid="preview-page">
      {/* SAMPLE WATERMARK */}
      <div className="pointer-events-none select-none fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[-24deg] text-[15vw] font-serif text-[#1A1915]/[0.04] tracking-tighter z-0 whitespace-nowrap">
        SAMPLE
      </div>

      {/* Preview banner */}
      <div className="border border-[#B8863C] bg-[#FAFAF9] p-4 flex items-center justify-between gap-4 flex-wrap relative z-10" data-testid="preview-banner">
        <div>
          <div className="eyebrow text-[#B8863C]">Preview · Sample Report</div>
          <div className="mt-1 text-sm text-[#1A1915]">This is illustrative. Your own report is generated from your answers — the diagnosis paragraphs and action plan below are masked.</div>
        </div>
        <Link to="/checkout" className="btn-primary text-xs" data-testid="preview-cta-banner" onClick={() => trackCta("preview_banner")}>Get your report — $97 <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} /></Link>
      </div>

      {/* Header — Editorial Dossier Masthead */}
      <header className="relative z-10 mt-10 border-t-2 border-[#1A1915]" data-testid="preview-header">
        {/* Masthead rule row */}
        <div className="flex items-center justify-between py-3 border-b border-[#1A1915]/30 text-[10px] uppercase tracking-[0.25em] text-[#1A1915]">
          <span className="font-semibold">AuthorityOS <span className="text-[#78716C] mx-1">·</span> Diagnostic Report</span>
          <span className="text-[#B8863C] font-semibold">No. 000&nbsp;·&nbsp;Sample</span>
        </div>

        {/* Headline + Dossier meta */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 py-10 border-b border-[#1A1915]">
          {/* Left — editorial headline */}
          <div className="md:col-span-8">
            <div className="eyebrow text-[#B8863C]">Verdict</div>
            <h1 className="mt-4 font-serif text-4xl lg:text-5xl text-[#1A1915] leading-[1.05] max-w-2xl" data-testid="preview-headline">
              The bottleneck sits in your Habits layer.
              <span className="block text-[#78716C] italic mt-2">Everything downstream is throttled here.</span>
            </h1>
          </div>

          {/* Right — dossier metadata */}
          <aside className="md:col-span-4 md:border-l md:border-[#1A1915]/20 md:pl-8 self-start" data-testid="preview-dossier">
            <dl className="grid grid-cols-2 gap-y-4 gap-x-4 text-[13px]">
              <dt className="eyebrow text-[#78716C]">Prepared for</dt>
              <dd className="text-[#1A1915] font-medium">Sample Client</dd>

              <dt className="eyebrow text-[#78716C]">Issued</dt>
              <dd className="text-[#1A1915] font-medium">Feb 2026</dd>

              <dt className="eyebrow text-[#78716C]">Instrument</dt>
              <dd className="text-[#1A1915] font-medium">v1.0</dd>

              <dt className="eyebrow text-[#78716C]">Model</dt>
              <dd className="text-[#1A1915] font-medium">Claude Sonnet 5</dd>

              <dt className="eyebrow text-[#78716C]">Report ID</dt>
              <dd className="text-[#1A1915] font-mono text-xs tracking-tight">AOS-SAMPLE-001</dd>
            </dl>
          </aside>
        </div>
      </header>

      {/* Composite */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#1A1915] border-t-0 relative z-10">
        <div className="lg:col-span-5 p-8 lg:border-r border-[#1A1915]">
          <div className="eyebrow">Authority Score</div>
          <div className="font-serif text-[8rem] text-[#1A1915] leading-[1.15] mt-4 tracking-tight">
            {SAMPLE_SCORES.authority_score.toFixed(1)}
          </div>
          <div className="text-sm text-[#78716C] mt-2">Composite / 5.0</div>
          <div className={`mt-2 text-xs uppercase tracking-widest font-semibold ${READING_TONE[SAMPLE_SCORES.authority_reading]}`}>{SAMPLE_SCORES.authority_reading}</div>
          <div className="mt-8 grid grid-cols-3 gap-4 text-xs">
            <div><div className="eyebrow">Habits</div><div className="font-serif text-3xl text-[#1A1915] mt-1">{SAMPLE_SCORES.habit_mean.toFixed(1)}</div></div>
            <div><div className="eyebrow">Traits</div><div className="font-serif text-3xl text-[#1A1915] mt-1">{SAMPLE_SCORES.trait_mean.toFixed(1)}</div></div>
            <div><div className="eyebrow">Effect</div><div className="font-serif text-3xl text-[#1A1915] mt-1">{SAMPLE_SCORES.effect_mean.toFixed(1)}</div></div>
          </div>
        </div>
        <div className="lg:col-span-7 border-t-4 border-[#B8863C] p-8 lg:border-t-4 lg:border-l border-[#1A1915]">
          <div className="eyebrow text-[#B8863C]">Bottleneck Identified</div>
          <div className="font-serif text-5xl text-[#1A1915] leading-tight mt-3">{SAMPLE_SCORES.bottleneck.zone}</div>
          <div className="text-sm text-[#78716C] mt-1">Habit zone · the causal lever</div>
          <div className="font-serif text-7xl text-[#B8863C] leading-none mt-4">{SAMPLE_SCORES.bottleneck.score.toFixed(1)}<span className="text-2xl text-[#1A1915]"> / 5.0</span></div>
          <p className="mt-6 text-base text-[#1A1915] leading-relaxed max-w-lg">
            Habits are the causal lever. Every gain in traits and effect is throttled by this zone. Address it first — the model is explicit: Space → Structure → Style → Relationships → Finances.
          </p>
        </div>
      </div>

      {/* Radars */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-t-0 border-[#1A1915] relative z-10">
        <div className="p-8 lg:border-r border-[#1A1915]" data-testid="preview-radar-habits">
          <div className="eyebrow">Habit Zones</div>
          <div className="mt-4 h-[380px] min-h-[300px] min-w-[280px]">
            <ResponsiveContainer width="100%" height="100%" minWidth={280} minHeight={300}>
              <RadarChart data={habitRadar}>
                <PolarGrid stroke="#1A1915" strokeOpacity={0.2} />
                <PolarAngleAxis dataKey="dim" tick={{ fill: "#1A1915", fontSize: 12 }} />
                <PolarRadiusAxis domain={[0, 5]} tick={{ fill: "#78716C", fontSize: 10 }} axisLine={false} />
                <Radar dataKey="score" stroke="#1A1915" strokeWidth={1.5} fill="#1A1915" fillOpacity={0.08} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="p-8 border-t lg:border-t-0 border-[#1A1915]" data-testid="preview-radar-effect">
          <div className="eyebrow">Effect Markers</div>
          <div className="mt-4 h-[380px] min-h-[300px] min-w-[280px]">
            <ResponsiveContainer width="100%" height="100%" minWidth={280} minHeight={300}>
              <RadarChart data={effectRadar}>
                <PolarGrid stroke="#1A1915" strokeOpacity={0.2} />
                <PolarAngleAxis dataKey="dim" tick={{ fill: "#1A1915", fontSize: 12 }} />
                <PolarRadiusAxis domain={[0, 5]} tick={{ fill: "#78716C", fontSize: 10 }} axisLine={false} />
                <Radar dataKey="score" stroke="#B8863C" strokeWidth={1.5} fill="#B8863C" fillOpacity={0.06} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Score matrix */}
      <div className="mt-0 border border-t-0 border-[#1A1915] relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3">
          {[
            { label: "Habits", data: SAMPLE_SCORES.habit_scores, highlight: SAMPLE_SCORES.bottleneck.zone },
            { label: "Traits", data: SAMPLE_SCORES.trait_scores },
            { label: "Effect", data: SAMPLE_SCORES.effect_scores },
          ].map((col, i) => (
            <div key={col.label} className={`p-6 ${i < 2 ? "md:border-r border-[#1A1915]" : ""} ${i > 0 ? "border-t md:border-t-0 border-[#1A1915]" : ""}`}>
              <div className="eyebrow">{col.label}</div>
              <ul className="mt-4 divide-y divide-[#1A1915]/15">
                {Object.entries(col.data).map(([k, v]) => {
                  const isBottleneck = k === col.highlight;
                  return (
                    <li key={k} className="py-3 flex items-center justify-between">
                      <span className={`text-sm ${isBottleneck ? "text-[#B8863C] font-semibold" : "text-[#1A1915]"}`}>{k}</span>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs uppercase tracking-widest ${READING_TONE[reading(v)]}`}>{reading(v)}</span>
                        <span className={`font-serif text-2xl ${isBottleneck ? "text-[#B8863C]" : "text-[#1A1915]"}`}>{v.toFixed(1)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Diagnosis — GATED */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-16 relative z-10" data-testid="preview-diagnosis-gate">
        <div className="md:col-span-4">
          <div className="eyebrow">Diagnosis</div>
          <h2 className="mt-4 font-serif text-4xl text-[#1A1915] leading-tight">Written verdict.</h2>
          <p className="mt-4 text-sm text-[#78716C]">Yours is personalized to your answers, dimensions, and situational composure pattern.</p>
        </div>
        <div className="md:col-span-8 relative">
          <div className="space-y-6">
            {SAMPLE_REPORT.paragraphs_masked.map((p, i) => {
              // Show first two sentences to convey more of the diagnostic depth; mask the rest.
              const sentences = p.match(/[^.!?]+[.!?]+\s*/g) || [p];
              const revealed = sentences.slice(0, 2).join("").trim();
              const masked = p.slice(revealed.length);
              return (
                <p key={i} className="text-base text-[#1A1915] leading-relaxed">
                  <span className="eyebrow mr-3 text-[#B8863C]">0{i + 1}</span>
                  {revealed}{masked ? " " : ""}
                  {masked && (
                    <span className="text-[#1A1915]/30 blur-[3px] select-none">
                      {masked}
                    </span>
                  )}
                </p>
              );
            })}
          </div>
          {/* Gate overlay */}
          <div className="mt-8 border border-[#1A1915] p-6 bg-[#FAFAF9]/95">
            <div className="flex items-start gap-3">
              <Lock className="w-4 h-4 mt-1 text-[#B8863C] shrink-0" strokeWidth={1.5} />
              <div>
                <div className="eyebrow text-[#B8863C]">Rest of the diagnosis is generated for you</div>
                <p className="mt-2 text-sm text-[#1A1915] leading-relaxed max-w-lg">
                  Your paragraphs will name your own bottleneck, your own trait constellation, your own situational-composure trigger. Sample above shows the depth — yours is personalized.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action plan — GATED */}
      <div className="mt-16 border border-[#1A1915] relative z-10" data-testid="preview-action-gate">
        <div className="p-6 border-b border-[#1A1915] flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow">Action Plan</div>
            <div className="font-serif text-3xl text-[#1A1915] mt-2">Prescription</div>
          </div>
          <div className="text-xs uppercase tracking-widest text-[#B8863C] font-semibold flex items-center gap-2">
            <Lock className="w-3 h-3" strokeWidth={1.5} /> Sample — yours is prescriptive to your bottleneck
          </div>
        </div>
        <div>
          {SAMPLE_REPORT.action_plan_masked.map((a, i) => (
            <div key={i} className={`grid grid-cols-12 gap-4 p-6 ${i < SAMPLE_REPORT.action_plan_masked.length - 1 ? "border-b border-[#1A1915]/20" : ""}`}>
              <div className="col-span-2 md:col-span-1 font-serif text-3xl text-[#1A1915]">0{a.priority}</div>
              <div className={`col-span-7 md:col-span-9 text-base leading-relaxed ${i === 0 ? "text-[#1A1915]" : "text-[#1A1915]/50 blur-[3px] select-none"}`}>{a.action}</div>
              <div className={`col-span-3 md:col-span-2 text-xs uppercase tracking-widest text-right ${i === 0 ? "text-[#78716C]" : "text-[#78716C]/50 blur-[2px] select-none"}`}>{a.timeframe}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Deeper reading */}
      <div className="mt-16 border-t-4 border-[#1A1915] pt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6 relative z-10">
        <div>
          <div className="eyebrow">Deeper Reading</div>
          <h2 className="mt-4 font-serif text-3xl text-[#1A1915] leading-tight max-w-xl">Read the full model — the 19-section e-book behind this diagnosis.</h2>
        </div>
        <Link to="/model" className="btn-secondary">
          <BookOpen className="mr-2 w-4 h-4" strokeWidth={1.5} /> Read the full model
        </Link>
      </div>

      {/* Final CTA */}
      <div className="mt-24 border-t border-[#1A1915]/15 pt-16 text-center relative z-10">
        <div className="eyebrow">Ready?</div>
        <h2 className="mt-4 font-serif text-5xl lg:text-6xl text-[#1A1915] leading-[0.95] max-w-3xl mx-auto">This is the shape. Yours is the substance.</h2>
        <p className="mt-6 text-lg text-[#78716C] max-w-xl mx-auto">Fifteen minutes. $97 today. One diagnosis you own for life.</p>
        <div className="mt-10 flex justify-center">
          <Link to="/checkout" className="btn-primary" data-testid="preview-cta-final" onClick={() => trackCta("preview_final")}>
            Take the diagnostic — $97 <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>
  );
}
